import { z } from 'zod';

export const APPOINTMENT_STATUSES = ['booked', 'completed', 'cancelled', 'no_show'] as const;
export const AppointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);
export type AppointmentStatus = z.infer<typeof AppointmentStatusSchema>;

export const APPOINTMENT_PROVIDERS = ['native', 'google'] as const;
export const AppointmentProviderSchema = z.enum(APPOINTMENT_PROVIDERS);
export type AppointmentProvider = z.infer<typeof AppointmentProviderSchema>;

export const CALENDAR_PROVIDERS = ['native', 'google'] as const;
export const CalendarProviderSchema = z.enum(CALENDAR_PROVIDERS);
export type CalendarProviderType = z.infer<typeof CalendarProviderSchema>;

export const CALENDAR_CONNECTION_STATUSES = ['active', 'error', 'revoked'] as const;
export const CalendarConnectionStatusSchema = z.enum(CALENDAR_CONNECTION_STATUSES);
export type CalendarConnectionStatus = z.infer<typeof CalendarConnectionStatusSchema>;

export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;
export const WeekdaySchema = z.number().int().min(0).max(6);
export type Weekday = z.infer<typeof WeekdaySchema>;

const SlotSchema = z.object({
  startsAt: z.number().int(),
  endsAt: z.number().int(),
});
export type Slot = z.infer<typeof SlotSchema>;

export const AvailabilityQuerySchema = z.object({
  startsAt: z.coerce.number().int(),
  endsAt: z.coerce.number().int(),
  userId: z.string().optional(),
  locationId: z.string().optional(),
});
export type AvailabilityQuery = z.infer<typeof AvailabilityQuerySchema>;

export const AvailabilityResponseSchema = z.object({
  slots: z.array(SlotSchema),
  timezone: z.string(),
});
export type AvailabilityResponse = z.infer<typeof AvailabilityResponseSchema>;

export const AppointmentSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  leadId: z.string().nullable(),
  contactId: z.string(),
  locationId: z.string().nullable(),
  assignedUserId: z.string().nullable(),
  provider: AppointmentProviderSchema,
  externalEventId: z.string().nullable(),
  startsAt: z.number().int(),
  endsAt: z.number().int(),
  status: AppointmentStatusSchema,
  notes: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Appointment = z.infer<typeof AppointmentSchema>;

export const AppointmentCreateBodySchema = z.object({
  leadId: z.string().optional(),
  contactId: z.string().min(1),
  locationId: z.string().optional(),
  assignedUserId: z.string().optional(),
  startsAt: z.number().int(),
  endsAt: z.number().int(),
  notes: z.string().optional(),
});
export type AppointmentCreateBody = z.infer<typeof AppointmentCreateBodySchema>;

export const AppointmentUpdateStatusSchema = z.object({
  status: AppointmentStatusSchema,
});
export type AppointmentUpdateStatus = z.infer<typeof AppointmentUpdateStatusSchema>;

export const AppointmentListQuerySchema = z.object({
  startsAt: z.coerce.number().int().optional(),
  endsAt: z.coerce.number().int().optional(),
  status: AppointmentStatusSchema.optional(),
  assignedUserId: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().positive().optional(),
});
export type AppointmentListQuery = z.infer<typeof AppointmentListQuerySchema>;

export const AppointmentListResponseSchema = z.object({
  items: z.array(AppointmentSchema),
  nextCursor: z.string().nullable(),
});
export type AppointmentListResponse = z.infer<typeof AppointmentListResponseSchema>;

export const AppointmentResponseSchema = AppointmentSchema;
export type AppointmentResponse = z.infer<typeof AppointmentResponseSchema>;

export const AvailabilityRuleSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  locationId: z.string().nullable(),
  weekday: WeekdaySchema,
  startMinute: z.number().int().min(0).max(1439),
  endMinute: z.number().int().min(0).max(1440),
  slotMinutes: z.number().int().min(1),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type AvailabilityRule = z.infer<typeof AvailabilityRuleSchema>;

export const AvailabilityRuleUpsertSchema = z.object({
  locationId: z.string().optional(),
  weekday: WeekdaySchema,
  startMinute: z.number().int().min(0).max(1439),
  endMinute: z.number().int().min(0).max(1440),
  slotMinutes: z.number().int().min(1).optional(),
});
export type AvailabilityRuleUpsert = z.infer<typeof AvailabilityRuleUpsertSchema>;

/**
 * `GET|POST /api/w/:slug/settings/availability-rules` — the weekly working-hours
 * editor loads and saves the whole set at once, so both directions speak
 * `{ rules }`. POST is replace-all for location-less rules: days absent from the
 * body are removed, which is how the UI expresses "closed that day".
 */
export const AvailabilityRulesResponseSchema = z.object({
  rules: z.array(AvailabilityRuleSchema),
});
export type AvailabilityRulesResponse = z.infer<typeof AvailabilityRulesResponseSchema>;

export const AvailabilityRulesUpdateBodySchema = z.object({
  rules: z.array(AvailabilityRuleUpsertSchema).max(21),
});
export type AvailabilityRulesUpdateBody = z.infer<typeof AvailabilityRulesUpdateBodySchema>;

export const CalendarConnectionSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  provider: CalendarProviderSchema,
  externalRef: z.string().nullable(),
  credentialsRef: z.string().nullable(),
  status: CalendarConnectionStatusSchema,
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type CalendarConnection = z.infer<typeof CalendarConnectionSchema>;

export const BookPublicSchema = z.object({
  slug: z.string().min(1),
});

export const PublicBookingRequestSchema = z.object({
  contactName: z.string().min(1).max(200),
  phoneE164: z.string().min(1).max(20),
  email: z.string().email().optional(),
  slotStartsAt: z.number().int(),
  locationId: z.string().optional(),
  assignedUserId: z.string().optional(),
  turnstileToken: z.string().min(1),
});
export type PublicBookingRequest = z.infer<typeof PublicBookingRequestSchema>;

export const PublicBookingTenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  timezone: z.string(),
});

export const PublicAvailabilityResponseSchema = z.object({
  tenant: PublicBookingTenantSchema,
  slots: z.array(SlotSchema),
  timezone: z.string(),
});
export type PublicAvailabilityResponse = z.infer<typeof PublicAvailabilityResponseSchema>;

export interface ListResponse<T> {
  items: T[];
  nextCursor: string | null;
}

export const LEAD_STAGE_TRANSITION_WITH_REVENUE = /^won$/;
